// Live's Animations group and its "Add Animation" popover (#266 part 1, spec animations.md): the
// clip's animations, each a row with its name, its IN or OUT, its duration, edit and delete; "+"
// or "Add Animation" opens the popover on its Presets tab (live's preset tiles, then a preset's
// settings, Timing and Easing), and "−" clears every one. The popover edits the shown clip alone,
// as live's does; "−" clears every chosen clip's.
import {
  ActionIcon,
  Button,
  CloseButton,
  NumberInput,
  Popover,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import { ArrowLeft, Minus, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import type {
  AnimationAxis,
  AnimationDirection,
  AnimationEasing,
  AnimationOrigin,
  AnimationPreset,
  BlockMode,
} from "../../api/generated/model";
import {
  AT_REST,
  EASINGS,
  KEYFRAME_FIELDS,
  MAX_ANIMATIONS,
  MAX_KEYFRAMES,
  MAX_ANIMATION_MS,
  MAX_ITERATIONS,
  PRESETS,
  STAGGERS,
  durationLabel,
  newAnimation,
  presetsFor,
  type Animation,
  type Keyframe,
  type PresetField,
} from "./animations";
import { PresetPreview } from "./AnimationPreview";
import { ColorField } from "./params";
import type { ImageClip, TextClip, TimelineEdit, VideoClip } from "./timeline";
import { newId } from "./timeline";
import classes from "./animations.module.css";
import editor from "./editor.module.css";

type Animated = VideoClip | ImageClip | TextClip;

/** The popover: adding, or editing one of the clip's animations by its id. */
type Opened = { editing: string | null } | null;

/**
 * Live's Animations group. `onEdit` sets every chosen clip (the panel's way); the popover's add,
 * edit and delete are the shown clip's alone, as live's popover edits only the first chosen clip.
 */
export function AnimationsSection({ clip, onEdit }: { clip: Animated; onEdit: (edit: TimelineEdit) => void }) {
  const [opened, setOpened] = useState<Opened>(null);
  const list = clip.animations;
  const lengthMs = clip.endMs - clip.startMs;
  // The shown clip alone: a "many" of one passes the panel's fan-out by unchanged.
  const setOwn = (animations: readonly Animation[]) =>
    onEdit({ type: "many", edits: [{ type: "setClip", id: clip.id, patch: { animations } }] });
  const open = list.length > 0;
  const editing = opened?.editing == null ? null : (list.find((each) => each.id === opened.editing) ?? null);
  const save = (animation: Animation) => {
    setOwn(
      list.some((each) => each.id === animation.id)
        ? list.map((each) => (each.id === animation.id ? animation : each))
        : [...list, animation],
    );
    setOpened(null);
  };
  return (
    <Popover
      opened={opened !== null}
      onChange={(next) => {
        if (!next) setOpened(null);
      }}
      position="left-start"
      offset={8}
      width={288}
      shadow="xl"
      trapFocus
      withinPortal
    >
      <Popover.Target>
        <div className={open ? editor.paramSection : undefined}>
          <div className={open ? editor.paramHeadRow : editor.paramFolded}>
            <Text className={editor.paramTitle}>Animations</Text>
            <ActionIcon
              variant="subtle"
              color="gray"
              size={24}
              aria-label={open ? "Remove Animations" : "Add Animations"}
              onClick={() => (open ? onEdit({ type: "setClip", id: clip.id, patch: { animations: [] } }) : setOpened({ editing: null }))}
            >
              {open ? <Minus size={14} /> : <Plus size={14} />}
            </ActionIcon>
          </div>
          {open ? (
            <Stack gap={4}>
              <Button
                variant="default"
                size="xs"
                fullWidth
                className={classes.addButton}
                leftSection={<Plus size={14} />}
                disabled={list.length >= MAX_ANIMATIONS}
                onClick={() => setOpened({ editing: null })}
              >
                Add Animation
              </Button>
              {list.map((animation) => {
                const info = PRESETS[animation.preset];
                const name = info.rowLabel ?? info.label;
                return (
                  <div key={animation.id} className={classes.row}>
                    <Text className={classes.rowName}>{name}</Text>
                    {/* Live shows IN on a combo too (it keeps a combo as an In), none on Custom. */}
                    {animation.preset === "custom" ? null : <span className={classes.badge}>{animation.mode ?? "in"}</span>}
                    <span className={classes.badge}>{durationLabel(animation.durationMs)}</span>
                    <span className={classes.rowActions}>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size={24}
                        aria-label={`Edit ${name}`}
                        onClick={() => setOpened({ editing: animation.id })}
                      >
                        <Pencil size={13} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size={24}
                        aria-label={`Delete ${name}`}
                        onClick={() => setOwn(list.filter((each) => each.id !== animation.id))}
                      >
                        <Trash2 size={13} />
                      </ActionIcon>
                    </span>
                  </div>
                );
              })}
            </Stack>
          ) : null}
        </div>
      </Popover.Target>
      <Popover.Dropdown className={classes.popover}>
        {opened === null ? null : (
          <AnimationPicker
            key={opened.editing ?? "new"}
            kind={clip.kind}
            lengthMs={lengthMs}
            editing={editing}
            onCancel={() => setOpened(null)}
            onSave={save}
          />
        )}
      </Popover.Dropdown>
    </Popover>
  );
}

const DIRECTIONS: readonly { value: AnimationDirection; label: string }[] = [
  { value: "left", label: "Left → Right" },
  { value: "right", label: "Right → Left" },
  { value: "top", label: "Top → Bottom" },
  { value: "bottom", label: "Bottom → Top" },
];
const SLIDE_DIRECTIONS: readonly { value: AnimationDirection; label: string }[] = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
];
const BLOCK_MODES: readonly { value: BlockMode; label: string }[] = [
  { value: "reveal", label: "Reveal (Expand & Contract)" },
  { value: "slide", label: "Slide (Fixed Width)" },
];
const AXES: readonly { value: AnimationAxis; label: string }[] = [
  { value: "vertical", label: "Vertical (top + bottom)" },
  { value: "horizontal", label: "Horizontal (left + right)" },
  { value: "both", label: "Both" },
];
const ORIGINS: readonly { value: AnimationOrigin; label: string }[] = [
  { value: "center", label: "Center" },
  { value: "top-left", label: "Top Left" },
  { value: "top-right", label: "Top Right" },
  { value: "bottom-left", label: "Bottom Left" },
  { value: "bottom-right", label: "Bottom Right" },
];

/**
 * The popover's inside: Presets (the tiles, then the picked preset's settings) or Custom
 * (its keyframes, #266 part 3); Cancel and Add or Save. Each pick starts from that preset's own
 * defaults (spec decision 3).
 */
function AnimationPicker(props: {
  kind: Animated["kind"];
  lengthMs: number;
  editing: Animation | null;
  onCancel: () => void;
  onSave: (animation: Animation) => void;
}) {
  const { kind, lengthMs, editing } = props;
  const editingCustom = editing?.preset === "custom";
  const [tab, setTab] = useState<"presets" | "custom">(editingCustom ? "custom" : "presets");
  const [draft, setDraft] = useState<Animation | null>(editing);
  const set = (change: Partial<Animation>) => setDraft((current) => (current === null ? current : { ...current, ...change }));
  const pick = (preset: AnimationPreset) => setDraft(newAnimation(editing?.id ?? newId(), preset, lengthMs));
  const info = draft === null ? null : PRESETS[draft.preset];
  return (
    <>
      <div className={classes.popoverHead}>
        <Text className={classes.popoverTitle}>{editing === null ? "Add Animation" : "Edit Animation"}</Text>
        <CloseButton size="sm" aria-label="Close" onClick={props.onCancel} />
      </div>
      <div className={classes.popoverBody}>
        <SegmentedControl
          fullWidth
          size="xs"
          value={tab}
          onChange={(next) => {
            const custom = next === "custom";
            setTab(custom ? "custom" : "presets");
            // Custom starts with its two empty stops (or the one being edited); Presets on its tiles.
            if (custom) setDraft(editingCustom ? editing : newAnimation(editing?.id ?? newId(), "custom", lengthMs));
            else setDraft(editing !== null && !editingCustom ? editing : null);
          }}
          data={[
            { value: "presets", label: "Presets" },
            { value: "custom", label: "Custom" },
          ]}
          mb="sm"
        />
        {tab === "custom" ? (
          draft === null ? null : (
            <>
              <CustomKeyframes animation={draft} kind={kind} onChange={set} />
              <Timing animation={draft} lengthMs={lengthMs} onChange={set} />
            </>
          )
        ) : draft === null || info === null ? (
          <ul className={classes.grid} aria-label="Animation presets">
            {presetsFor(kind).map((preset) => (
              <li key={preset}>
                <button
                  type="button"
                  className={classes.tile}
                  aria-label={PRESETS[preset].spoken ?? PRESETS[preset].label}
                  onClick={() => pick(preset)}
                >
                  <PresetPreview preset={preset} />
                  <span className={classes.tileName}>{PRESETS[preset].label}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <PresetSettings animation={draft} fields={info.fields} lengthMs={lengthMs} onBack={() => setDraft(null)} onChange={set} />
        )}
      </div>
      {draft !== null ? (
        <div className={classes.popoverFoot}>
          <Button variant="default" size="xs" onClick={props.onCancel}>
            Cancel
          </Button>
          {/* Live's: Custom adds once a stop sets something. */}
          <Button
            size="xs"
            disabled={draft.preset === "custom" && !draft.keyframes.some((stop) => Object.keys(stop.values).length > 0)}
            onClick={() => props.onSave(draft)}
          >
            {editing === null ? "Add" : "Save"}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Text className={classes.fieldLabel}>{label}</Text>
      {children}
    </Stack>
  );
}

/** A preset's settings, in live's order: Mode, its mask or slide box, Timing and Easing. */
function PresetSettings(props: {
  animation: Animation;
  fields: readonly PresetField[];
  lengthMs: number;
  onBack: () => void;
  onChange: (change: Partial<Animation>) => void;
}) {
  const { animation, fields, lengthMs, onChange } = props;
  const info = PRESETS[animation.preset];
  const has = (field: PresetField) => fields.includes(field);
  const select = <T extends string>(label: string, value: T, data: readonly { value: T; label: string }[], key: keyof Animation) => (
    <Field label={label}>
      <Select
        // In the popover: an option picked outside it would close it.
        comboboxProps={{ withinPortal: false }}
        aria-label={label}
        size="xs"
        allowDeselect={false}
        data={data.map((each) => ({ ...each }))}
        value={value}
        onChange={(next) => {
          if (next !== null) onChange({ [key]: next } as Partial<Animation>);
        }}
      />
    </Field>
  );
  const slider = (label: string, value: number, min: number, max: number, step: number, key: keyof Animation) => (
    <Field label={label}>
      <Slider
        min={min}
        max={max}
        step={step}
        value={value}
        label={null}
        thumbLabel={label.replace(/ \(.*\)$| \d+%$| [\d.]+s$/, "")}
        onChange={(next) => onChange({ [key]: next } as Partial<Animation>)}
      />
    </Field>
  );
  const maskBox = fields.some((field) => field !== "slide" && field !== "textSlide" && field !== "block");
  return (
    <>
      <div className={classes.detailHead}>
        <Button variant="subtle" size="xs" leftSection={<ArrowLeft size={14} />} onClick={props.onBack}>
          Back
        </Button>
        <Text size="xs" fw={600}>
          {info.label}
        </Text>
      </div>
      {info.group === "combo" ? null : (
        <Field label="Mode">
          <SegmentedControl
            size="xs"
            value={animation.mode ?? "in"}
            onChange={(next) => onChange({ mode: next as "in" | "out" })}
            data={[
              { value: "in", label: "In" },
              { value: "out", label: "Out" },
            ]}
          />
        </Field>
      )}
      {maskBox ? (
        <div className={classes.box}>
          {has("direction") ? select("Direction", animation.direction, DIRECTIONS, "direction") : null}
          {has("angle") ? slider(`Angle (${animation.angle}°)`, animation.angle, 0, 360, 1, "angle") : null}
          {has("axis") ? select("Axis", animation.axis, AXES, "axis") : null}
          {has("origin") ? select("Origin", animation.origin, ORIGINS, "origin") : null}
          {has("margins") ? slider(`X Margin Offset (${animation.xMargin}px)`, animation.xMargin, 0, 500, 5, "xMargin") : null}
          {has("margins") ? slider(`Y Margin Offset (${animation.yMargin}px)`, animation.yMargin, 0, 500, 5, "yMargin") : null}
          {has("borderRadius")
            ? slider(`Mask Border Radius (${animation.borderRadius}px)`, animation.borderRadius, 0, 200, 1, "borderRadius")
            : null}
          {has("initial")
            ? slider(`Initial ${Math.round(animation.initial * 100)}%`, animation.initial, 0, 0.9, 0.01, "initial")
            : null}
        </div>
      ) : null}
      {has("slide") || has("wordSlide") ? (
        <div className={classes.box}>
          {select("Direction", animation.direction, SLIDE_DIRECTIONS, "direction")}
          <Field label="Distance (px)">
            <NumberInput
              aria-label="Distance (px)"
              size="xs"
              min={-10_000}
              max={10_000}
              clampBehavior="strict"
              value={animation.distance}
              onChange={(next) => {
                if (typeof next === "number") onChange({ distance: next });
              }}
            />
          </Field>
        </div>
      ) : null}
      {has("textSlide") ? <div className={classes.box}>{select("Direction", animation.direction, SLIDE_DIRECTIONS, "direction")}</div> : null}
      {has("stagger") ? (
        <div className={classes.box}>
          {slider(
            `Stagger ${(animation.stagger ?? STAGGERS[animation.preset]?.each ?? 0).toFixed(2)}s`,
            animation.stagger ?? STAGGERS[animation.preset]?.each ?? 0,
            0,
            1.5,
            0.01,
            "stagger",
          )}
        </div>
      ) : null}
      {has("block") ? (
        <div className={classes.box}>
          {select("Direction", animation.direction, DIRECTIONS, "direction")}
          {select("Mode", animation.blockMode, BLOCK_MODES, "blockMode")}
          {animation.blockMode === "slide"
            ? slider(`Block Size ${Math.round(animation.blockSize * 100)}%`, animation.blockSize, 0.1, 1, 0.05, "blockSize")
            : null}
          <Field label="Block Color">
            <ColorField
              label="Block Color"
              value={animation.blockColor}
              withinPortal={false}
              onCommit={(blockColor) => onChange({ blockColor })}
            />
          </Field>
          <Field label="Inverted Text">
            <ColorField
              label="Inverted Text"
              value={animation.invertedColor}
              withinPortal={false}
              onCommit={(invertedColor) => onChange({ invertedColor })}
            />
          </Field>
        </div>
      ) : null}
      <Timing animation={animation} lengthMs={lengthMs} onChange={onChange} />
    </>
  );
}

/** Live's Timing (Duration, Delay greyed for an Out, Iterations) and Easing. */
function Timing({
  animation,
  lengthMs,
  onChange,
}: {
  animation: Animation;
  lengthMs: number;
  onChange: (change: Partial<Animation>) => void;
}) {
  const out = animation.mode === "out";
  return (
    <>
      <Text className={classes.sectionLabel}>Timing</Text>
      <div className={classes.timing}>
        <Field label="Duration (ms)">
          <NumberInput
            aria-label="Duration (ms)"
            size="xs"
            hideControls
            min={0}
            max={MAX_ANIMATION_MS}
            allowDecimal={false}
            clampBehavior="strict"
            value={animation.durationMs}
            onChange={(next) => {
              if (typeof next === "number") onChange({ durationMs: next });
            }}
          />
        </Field>
        <Field label="Delay (ms)">
          {/* Live's Out ends at the clip's end: its delay is that, shown and not set. */}
          <NumberInput
            aria-label="Delay (ms)"
            size="xs"
            hideControls
            min={0}
            max={MAX_ANIMATION_MS}
            allowDecimal={false}
            clampBehavior="strict"
            disabled={out}
            value={out ? Math.max(0, lengthMs - animation.durationMs) : animation.delayMs}
            onChange={(next) => {
              if (typeof next === "number") onChange({ delayMs: next });
            }}
          />
        </Field>
        <Field label="Iterations">
          <NumberInput
            aria-label="Iterations"
            size="xs"
            hideControls
            min={1}
            max={MAX_ITERATIONS}
            allowDecimal={false}
            clampBehavior="strict"
            value={animation.iterations}
            onChange={(next) => {
              if (typeof next === "number") onChange({ iterations: next });
            }}
          />
        </Field>
      </div>
      <Text className={classes.sectionLabel}>Easing</Text>
      <Select
        // In the popover: an option picked outside it would close it.
        comboboxProps={{ withinPortal: false }}
        aria-label="Easing"
        size="xs"
        allowDeselect={false}
        data={EASINGS.map((each) => ({ ...each }))}
        value={animation.easing}
        onChange={(next) => {
          if (next !== null) onChange({ easing: next as AnimationEasing });
        }}
      />
    </>
  );
}

/**
 * Where live's "Add Stop" puts a stop: at 50 % with fewer than two; else in the middle of the
 * widest gap wider than 5 points, else 10 after the last; the next free point after that. None
 * when it would land on the 100 % stop (live's then empties that stop).
 */
export function nextStop(stops: readonly number[]): number | null {
  const sorted = [...stops].sort((one, other) => one - other);
  let at: number;
  if (sorted.length < 2) at = 50;
  else {
    let widest = 0;
    let middle = 0;
    for (let index = 1; index < sorted.length; index += 1) {
      const gap = sorted[index]! - sorted[index - 1]!;
      if (gap > widest) {
        widest = gap;
        middle = Math.round((sorted[index]! + sorted[index - 1]!) / 2);
      }
    }
    at = widest > 5 ? middle : Math.min(100, sorted.at(-1)! + 10);
  }
  while (sorted.includes(at) && at < 100) at += 1;
  return sorted.includes(at) ? null : at;
}

/** Live's Custom tab: its stops, each a card of properties with their sliders; Add Stop; Mirror. */
function CustomKeyframes({
  animation,
  kind,
  onChange,
}: {
  animation: Animation;
  kind: Animated["kind"];
  onChange: (change: Partial<Animation>) => void;
}) {
  const stops = [...animation.keyframes].sort((one, other) => one.at - other.at);
  const setStops = (keyframes: readonly Keyframe[]) => onChange({ keyframes });
  const at = nextStop(stops.map((stop) => stop.at));
  const change = (stop: Keyframe, next: Keyframe) => setStops(stops.map((each) => (each === stop ? next : each)));
  return (
    <Stack gap={8}>
      <div className={classes.detailHead}>
        <Text className={classes.sectionLabel}>Keyframes</Text>
        <Button
          variant="default"
          size="xs"
          disabled={at === null || stops.length >= MAX_KEYFRAMES}
          onClick={() => {
            if (at !== null) setStops([...stops, { at, values: {} }]);
          }}
        >
          Add Stop
        </Button>
      </div>
      {stops.map((stop) => (
        <StopCard
          key={stop.at}
          stop={stop}
          taken={stops.map((each) => each.at)}
          onChange={(next) => change(stop, next)}
          onRemove={() => setStops(stops.filter((each) => each !== stop))}
        />
      ))}
      {/* Live's Mirror switch: the picture tiled with flipped copies; a text has none. */}
      {kind === "text" ? null : (
        <Switch size="xs" label="Mirror" checked={animation.mirror} onChange={(event) => onChange({ mirror: event.currentTarget.checked })} />
      )}
    </Stack>
  );
}

/** One stop: its place (fixed at 0 % and 100 %), its properties, and "+ Add Property". */
function StopCard(props: {
  stop: Keyframe;
  taken: readonly number[];
  onChange: (stop: Keyframe) => void;
  onRemove: () => void;
}) {
  const { stop, taken, onChange } = props;
  const ends = stop.at === 0 || stop.at === 100;
  const set = Object.keys(stop.values) as (keyof Keyframe["values"])[];
  const fields = KEYFRAME_FIELDS.filter((field) => set.includes(field.key));
  const summary = fields.length === 0 ? "No properties" : fields.map((field) => field.label).join(", ");
  const without = (key: keyof Keyframe["values"]) =>
    Object.fromEntries(Object.entries(stop.values).filter(([name]) => name !== key)) as Keyframe["values"];
  return (
    <fieldset className={classes.stop} aria-label={`Stop ${stop.at}%`}>
      <div className={classes.row}>
        {ends ? (
          <Text size="xs" fw={700}>
            {stop.at}%
          </Text>
        ) : (
          <NumberInput
            aria-label={`Stop at ${stop.at}%`}
            size="xs"
            w={64}
            min={1}
            max={99}
            suffix="%"
            allowDecimal={false}
            clampBehavior="strict"
            value={stop.at}
            onBlur={(event) => {
              const typed = Number.parseInt(event.currentTarget.value, 10);
              const next = Number.isFinite(typed) ? Math.min(99, Math.max(1, typed)) : 1;
              if (next !== stop.at && !taken.includes(next)) onChange({ ...stop, at: next });
            }}
          />
        )}
        <Text className={classes.rowName}>{summary}</Text>
        {ends ? null : (
          <ActionIcon variant="subtle" color="gray" size={24} aria-label={`Remove stop ${stop.at}%`} onClick={props.onRemove}>
            <Trash2 size={13} />
          </ActionIcon>
        )}
      </div>
      {fields.length === 0 ? (
        <Text size="xs" c="dimmed">
          No properties — add one below
        </Text>
      ) : null}
      {fields.map((field) => {
        const value = stop.values[field.key] ?? AT_REST[field.key];
        const setValue = (next: number) => onChange({ ...stop, values: { ...stop.values, [field.key]: next } });
        return (
          <Stack key={field.key} gap={2}>
            <div className={classes.row}>
              <Text className={classes.rowName}>{field.label}</Text>
              <NumberInput
                aria-label={`${field.label} at ${stop.at}%`}
                size="xs"
                w={72}
                hideControls
                min={-10_000}
                max={10_000}
                clampBehavior="strict"
                value={value}
                onChange={(next) => {
                  if (typeof next === "number") setValue(next);
                }}
              />
              <ActionIcon
                variant="subtle"
                color="gray"
                size={20}
                aria-label={`Remove ${field.label} at ${stop.at}%`}
                onClick={() => onChange({ ...stop, values: without(field.key) })}
              >
                <X size={12} />
              </ActionIcon>
            </div>
            <Slider
              min={field.min}
              max={field.max}
              step={field.step}
              value={Math.min(field.max, Math.max(field.min, value))}
              label={null}
              thumbLabel={`${field.label} at ${stop.at}%`}
              onChange={setValue}
            />
          </Stack>
        );
      })}
      <Select
        // In the popover: an option picked outside it would close it.
        comboboxProps={{ withinPortal: false }}
        aria-label={`Add a property at ${stop.at}%`}
        size="xs"
        placeholder="+ Add Property"
        value={null}
        data={KEYFRAME_FIELDS.filter((field) => !set.includes(field.key)).map((field) => ({ value: field.key, label: field.label }))}
        onChange={(next) => {
          const field = KEYFRAME_FIELDS.find((each) => each.key === next);
          if (field !== undefined) onChange({ ...stop, values: { ...stop.values, [field.key]: AT_REST[field.key] } });
        }}
      />
    </fieldset>
  );
}
